import { useState } from "react";
import { capturePartnerRef, getPartnerCode } from "../../../utils/partnerRef";
import { Link } from "react-router-dom";
import PlatformAuthShell from "./PlatformAuthShell";
import AuthField from "./AuthField";
import Button from "../../ui/Button";
import ConsentCheckbox from "../../Legal/ConsentCheckbox";
import { LegalDocLink } from "../../Legal/LegalDocLink";
import { LEGAL_PUBLIC_ENABLED } from "../../../constants/legalPaths";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { subdomainFromClubName } from "../../../utils/clubSubdomain";
import { TRIAL_DAYS } from "../../../constants/trial";
import { formatPhoneDisplay } from "../../../utils/phoneMask";
import authAPI from "../../../services/authApi";
import a from "./auth.module.css";

const STEP_LABELS_FULL = ["Клуб", "Поддомен", "Аккаунт", "Готово"];
const STEP_LABELS_ADDITIONAL = ["Клуб", "Поддомен", "Готово"];

const EMPTY = {
  clinicName: "",
  subdomain: "",
  login: "",
  password: "",
  fullName: "",
  phone: "",
};

function subdomainPreview(sub) {
  const s = (sub || "club").toLowerCase().replace(/[^a-z0-9-]/g, "");
  return s || "club";
}

function subdomainHostLabel(sub) {
  try {
    return new URL(buildClinicPortalUrl(subdomainPreview(sub), "/")).host;
  } catch {
    return `${subdomainPreview(sub)}.localhost`;
  }
}

function hasPartnerOffer() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("ref") || params.get("partner") || "";
  if (raw && capturePartnerRef(raw)) return true;
  return Boolean(getPartnerCode());
}

function buildOwnerLoginUrl(email, nextPath = "/account") {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (nextPath) params.set("next", nextPath);
  const q = params.toString();
  return q ? `/account/login?${q}` : "/account/login";
}

export default function AuthClubRegisterFlow({
  loading,
  error,
  onSubmit,
  backTo = "/",
  backLabel = "Назад",
  onLogin,
  variant = "full",
  ownerEmail = "",
  ownerName = "",
  externalExistingAccount = null,
  /** Владелец уже принял актуальную оферту/согласие — не спрашивать снова */
  legalAlreadyAccepted = false,
  /** Название клуба, введённое на лендинге: шаг 1 пропускается */
  initialClubName = "",
}) {
  const isAdditional = variant === "additional";
  const askLegal =
    LEGAL_PUBLIC_ENABLED && !(isAdditional && legalAlreadyAccepted);
  const stepTotal = isAdditional ? 3 : 4;
  const stepLabels = isAdditional ? STEP_LABELS_ADDITIONAL : STEP_LABELS_FULL;
  const reviewStep = stepTotal;

  const presetClubName = String(initialClubName || "").trim();
  const [step, setStep] = useState(presetClubName.length >= 2 ? 2 : 1);
  const [data, setData] = useState(() => ({
    ...EMPTY,
    clinicName: presetClubName,
    subdomain: subdomainFromClubName(presetClubName),
    login: ownerEmail || "",
    fullName: ownerName || "",
  }));
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [emailCheckLoad, setEmailCheckLoad] = useState(false);
  const [existingAccount, setExistingAccount] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [consentPd, setConsentPd] = useState(false);
  const [partnerOffer] = useState(() => !isAdditional && hasPartnerOffer());

  const patch = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const blockedAccount =
    externalExistingAccount?.email || existingAccount?.email || null;

  const handleClinicNameChange = (value) => {
    setData((d) => {
      const next = { ...d, clinicName: value };
      if (!subdomainTouched) {
        next.subdomain = subdomainFromClubName(value);
      }
      return next;
    });
  };

  const handleSubdomainChange = (value) => {
    setSubdomainTouched(true);
    patch("subdomain", value.replace(/\s/g, "").toLowerCase());
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return data.clinicName.trim().length >= 2;
      case 2:
        return /^[a-z0-9-]{2,32}$/.test(data.subdomain);
      case 3:
        if (isAdditional) return true;
        return (
          /\S+@\S+\.\S+/.test(data.login) &&
          data.password.length >= 8 &&
          data.fullName.trim().length >= 2
        );
      default:
        return true;
    }
  };

  const checkOwnerEmail = async (email) => {
    const normalized = String(email || "").trim().toLowerCase();
    if (!/\S+@\S+\.\S+/.test(normalized)) return false;
    setEmailCheckLoad(true);
    try {
      const res = await authAPI.checkRegisterOwnerEmail(normalized);
      if (res?.exists) {
        setExistingAccount({ email: normalized });
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setEmailCheckLoad(false);
    }
  };

  const next = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    if (!isAdditional && step === 3) {
      const taken = await checkOwnerEmail(data.login);
      if (taken) return;
    }

    if (step < reviewStep) setStep(step + 1);
  };

  const submit = (e) => {
    e.preventDefault();
    if (askLegal && (!acceptTerms || !consentPd)) return;
    onSubmit({
      ...data,
      ...(LEGAL_PUBLIC_ENABLED
        ? {
            accept_terms: askLegal ? true : legalAlreadyAccepted,
            consent_pd: askLegal ? true : legalAlreadyAccepted,
          }
        : {}),
    });
  };

  const loginPath = buildOwnerLoginUrl(blockedAccount);

  const footerLink =
    step < reviewStep && !blockedAccount ? (
      <div className={a.footerLinks}>
        {onLogin ? (
          <span
            role="button"
            tabIndex={0}
            onClick={() => onLogin()}
            onKeyDown={(e) => e.key === "Enter" && onLogin()}
          >
            Уже зарегистрированы? <span>Войти</span>
          </span>
        ) : (
          <Link to="/find">
            Уже зарегистрированы? <span>Найти и войти</span>
          </Link>
        )}
      </div>
    ) : null;

  if (blockedAccount) {
    return (
      <PlatformAuthShell
        backTo={backTo}
        backLabel={backLabel}
        title="У вас уже есть аккаунт"
        subtitle="Войдите в кабинет владельца — там можно управлять клубами."
      >
        <p style={{ marginBottom: 16, lineHeight: 1.5, color: "var(--text-muted, #64748b)" }}>
          Аккаунт с адресом <strong>{blockedAccount}</strong> уже зарегистрирован.
        </p>
        <div className={a.actions}>
          <Button
            type="button"
            block
            size="lg"
            onClick={() => {
              if (onLogin) onLogin(blockedAccount);
              else window.location.assign(loginPath);
            }}
          >
            Войти
          </Button>
          {!isAdditional ? (
            <Button
              type="button"
              variant="ghost"
              block
              onClick={() => {
                setExistingAccount(null);
                setStep(3);
              }}
            >
              Изменить email
            </Button>
          ) : null}
        </div>
      </PlatformAuthShell>
    );
  }

  return (
    <PlatformAuthShell
      backTo={backTo}
      backLabel={backLabel}
      title={isAdditional ? "Новый клуб" : "Подключите клуб"}
      subtitle={
        partnerOffer
          ? `Скидка 20% на первые 3 месяца подписки. Сначала ${TRIAL_DAYS} дней демо.`
          : isAdditional
            ? "Укажите название и адрес — аккаунт владельца уже есть."
            : `${TRIAL_DAYS} дней демо-доступа — журнал, клиенты, финансы и кабинет спортсменов.`
      }
      stepCurrent={step}
      stepTotal={stepTotal}
      stepLabels={stepLabels}
      footer={footerLink}
    >
      {partnerOffer ? (
        <div className={a.partnerOffer}>
          <strong>−20%</strong>
          <p>На первые 3 месяца подписки. Скидка уже закреплена за этой регистрацией.</p>
        </div>
      ) : null}

      {error ? (
        <div className={a.errorBox}>
          {typeof error === "string" ? error : error?.message || error?.error}
        </div>
      ) : null}

      {step === 1 && (
        <form onSubmit={next}>
          <AuthField
            label="Название клуба"
            value={data.clinicName}
            onChange={(e) => handleClinicNameChange(e.target.value)}
            placeholder="Секция дзюдо «Байтим»"
            hint="Если название из одного слова, поддомен подставится автоматически"
            required
          />
          <div className={a.actions}>
            <Button type="submit" block size="lg" disabled={!validateStep()}>
              Продолжить
            </Button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={next}>
          <AuthField
            label="Адрес клуба (поддомен)"
            value={data.subdomain}
            onChange={(e) => handleSubdomainChange(e.target.value)}
            placeholder="baytim"
            hint="Латиница, цифры и дефис — a-z, 0-9"
            required
          />
          <div className={a.previewBox}>
            <span className={a.previewLabel}>Ваш адрес</span>
            <span className={a.previewValue}>
              {subdomainHostLabel(data.subdomain)}
            </span>
          </div>
          <div className={a.actions}>
            <Button type="submit" block size="lg" disabled={!validateStep()}>
              Продолжить
            </Button>
            <Button type="button" variant="ghost" block onClick={() => setStep(1)}>
              Назад
            </Button>
          </div>
        </form>
      )}

      {step === 3 && !isAdditional && (
        <form onSubmit={next}>
          <AuthField
            label="ФИО"
            value={data.fullName}
            onChange={(e) => patch("fullName", e.target.value)}
            placeholder="Иванов Иван Иванович"
            autoComplete="name"
            required
          />
          <AuthField
            label="Email"
            type="email"
            value={data.login}
            onChange={(e) => patch("login", e.target.value)}
            placeholder="you@club.ru"
            autoComplete="email"
            hint="На этот адрес придут уведомления и доступ в кабинет"
            required
          />
          <AuthField
            label="Телефон"
            asPhone
            value={data.phone}
            onChange={(v) => patch("phone", v)}
            placeholder="+7 (___) ___-__-__"
            autoComplete="tel"
            hint="Необязательно"
          />
          <AuthField
            label="Пароль"
            type="password"
            value={data.password}
            onChange={(e) => patch("password", e.target.value)}
            placeholder="Минимум 8 символов"
            autoComplete="new-password"
            minLength={8}
            required
          />
          <div className={a.actions}>
            <Button
              type="submit"
              block
              size="lg"
              disabled={!validateStep() || emailCheckLoad}
            >
              {emailCheckLoad ? "Проверяем…" : "Продолжить"}
            </Button>
            <Button type="button" variant="ghost" block onClick={() => setStep(2)}>
              Назад
            </Button>
          </div>
        </form>
      )}

      {step === reviewStep && (
        <form onSubmit={submit}>
          <div className={a.reviewBox}>
            <div className={a.reviewRow}>
              <span className={a.reviewLabel}>Клуб</span>
              <span className={a.reviewValue}>{data.clinicName}</span>
            </div>
            <div className={a.reviewRow}>
              <span className={a.reviewLabel}>Адрес</span>
              <span className={a.reviewValue}>
                {subdomainHostLabel(data.subdomain)}
              </span>
            </div>
            {!isAdditional ? (
              <div className={a.reviewRow}>
                <span className={a.reviewLabel}>Владелец</span>
                <span className={a.reviewValue}>{data.fullName}</span>
                <span className={a.reviewValueMuted}>{data.login}</span>
                {data.phone ? (
                  <span className={a.reviewValueMuted}>
                    {formatPhoneDisplay(data.phone)}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          {askLegal ? (
            <>
              <ConsentCheckbox
                id="club-accept-offer"
                checked={acceptTerms}
                onChange={setAcceptTerms}
                disabled={loading}
              >
                Принимаю{" "}
                <LegalDocLink doc="offer">
                  Пользовательское соглашение (оферту)
                </LegalDocLink>
                {" и "}
                <LegalDocLink doc="payments">Условия платежей</LegalDocLink>
              </ConsentCheckbox>
              <ConsentCheckbox
                id="club-consent-pd"
                checked={consentPd}
                onChange={setConsentPd}
                disabled={loading}
              >
                Даю согласие на обработку моих персональных данных согласно{" "}
                <LegalDocLink doc="ownerConsent">тексту согласия</LegalDocLink>
                {" ("}
                <LegalDocLink doc="privacy">политика конфиденциальности</LegalDocLink>
                )
              </ConsentCheckbox>
            </>
          ) : null}
          <div className={a.actions}>
            <Button
              type="submit"
              block
              size="lg"
              disabled={loading || (askLegal && (!acceptTerms || !consentPd))}
            >
              {loading ? "Создаём…" : "Создать клуб"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              block
              onClick={() => setStep(isAdditional ? 2 : 3)}
              disabled={loading}
            >
              Назад
            </Button>
          </div>
        </form>
      )}
    </PlatformAuthShell>
  );
}
